import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchWorkspaces,
  fetchWorkspace,
  createWorkspace,
  updateWorkspace,
  deleteWorkspace,
} from '../api/workspaces.api';
import { workspaceKeys } from './queryKeys';
import type { CreateWorkspaceData, UpdateWorkspaceData, Workspace } from '../types/workspace.types';

export function useWorkspaces() {
  return useQuery({
    queryKey: workspaceKeys.lists(),
    queryFn: fetchWorkspaces,
    staleTime: 5 * 60 * 1000,
  });
}

export function useWorkspace(id: number) {
  return useQuery({
    queryKey: workspaceKeys.detail(id),
    queryFn: () => fetchWorkspace(id),
    enabled: id > 0,
    staleTime: 60 * 1000,
  });
}

export function useCreateWorkspace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateWorkspaceData) => createWorkspace(data),
    onSuccess: (workspace) => {
      const createdWorkspace: Workspace = {
        ...workspace,
        workspaceRole: workspace.workspaceRole ?? 'OWNER',
      };

      queryClient.setQueryData(workspaceKeys.detail(createdWorkspace.id), createdWorkspace);
      queryClient.setQueryData<Workspace[]>(workspaceKeys.lists(), (current = []) => {
        const existingIndex = current.findIndex((w) => w.id === createdWorkspace.id);
        if (existingIndex === -1) {
          return [...current, createdWorkspace];
        }

        return current.map((w) => (w.id === createdWorkspace.id ? createdWorkspace : w));
      });
      queryClient.invalidateQueries({ queryKey: workspaceKeys.lists() });
    },
  });
}

export function useUpdateWorkspace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateWorkspaceData }) =>
      updateWorkspace(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: workspaceKeys.lists() });
    },
  });
}

export function useDeleteWorkspace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteWorkspace(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.lists() });
    },
  });
}
